import { useContext, useMemo, type JSX } from "react";
import {
  BIKE_TYPE_VALUES_V2,
  type FrameContractDTO,
  type BikeTypeV2,
  type LeasingOptionType,
  getVSTDependantServiceRateFromFramecontract,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { SimpleRadioGroup } from "../SimpleRadioGroup";
import { useI18n } from "../../i18n";
import { InputDataUIModel } from "../CalculatorForm";
import { AppContext } from "../../state";

type ServiceSelectProps = {
  services: LeasingOptionType[];
  inputValues: InputDataUIModel;
  frameContract: FrameContractDTO;
  onValueChanged: (selectedService: LeasingOptionType | undefined) => void;
  ariaLabelledBy?: string;
};

const ServiceSelect = (props: ServiceSelectProps): JSX.Element => {
  const { services, inputValues, frameContract, onValueChanged, ariaLabelledBy } = props;
  const { t, formatNumber } = useI18n();
  const state = useContext(AppContext);

  const onChange = (newSelectionId: string) => {
    if (onValueChanged) {
      const selectedService = services.find((service) => service.id === Number.parseInt(newSelectionId));
      onValueChanged(selectedService);
    }
  };

  const serviceTypeOptions = useMemo(() => {
    const bikeType = (
      BIKE_TYPE_VALUES_V2.includes(inputValues.fahrradtyp as BikeTypeV2) ? inputValues.fahrradtyp : "default_bike"
    ) as BikeTypeV2;

    return services.map((service: LeasingOptionType) => {
      const serviceRate = getVSTDependantServiceRateFromFramecontract(
        frameContract.bike_services.leasing_options,
        bikeType,
        service.service_type,
        service.service_subtype,
        inputValues.kaufpreis,
        inputValues.vorsteuerabzugsberechtigt,
      );

      if (service.service_type === false) {
        return {
          name: t(`serviceOptions.${service.service_type}.${service.service_subtype}`),
          value: service.id,
        };
      }

      const displayedServiceRate = getDisplayedServiceRate(
        serviceRate,
        frameContract.grant ?? 0,
        state.configuration.deductGrantFromServicePrices,
      );

      return {
        name: t(`serviceOptions.${service.service_type}.${service.service_subtype}`, {
          price: formatNumber(displayedServiceRate, { digits: 2 }),
        }),
        value: service.id,
      };
    });
  }, [services, inputValues, frameContract, t, formatNumber, state.configuration.deductGrantFromServicePrices]);

  const currentValue = useMemo(() => {
    return (
      services.find(
        (service) =>
          service.service_type === inputValues.serviceType && service.service_subtype === inputValues.serviceSubType,
      ) || services[0]
    );
  }, [services, inputValues]);
  return (
    <SimpleRadioGroup
      options={serviceTypeOptions}
      variant="toggleButton"
      onValueChangedParse={onChange}
      name="serviceSelect"
      value={currentValue.id.toString()}
      ariaLabelledBy={ariaLabelledBy}
    />
  );
};

const getDisplayedServiceRate = (serviceRate: number, grant: number, deductGrantFromServicePrices: boolean): number => {
  if (!deductGrantFromServicePrices) {
    return serviceRate;
  }

  return Math.max(serviceRate - grant, 0);
};

export { ServiceSelect };
